import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-login", form);
      localStorage.setItem("gf_token", data.token);
      localStorage.setItem("gf_role", "admin");
      toast.success(`Welcome, ${data.user.full_name}`);
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Admin sign-in failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-neutral-950 text-neutral-100">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2 mb-6 justify-center text-primary">
          <ShieldCheck className="w-6 h-6"/>
          <span className="font-display text-2xl">GrantForge Admin</span>
        </div>
        <form onSubmit={submit} className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 shadow-2xl" data-testid="admin-login-form">
          <div className="overline text-primary">Restricted access</div>
          <h1 className="font-display text-3xl mt-2 leading-tight">Administrator sign-in</h1>
          <p className="text-neutral-400 text-sm mt-2">This page is for GrantForge administrators only. Regular users, please use the <Link to="/login" className="text-primary hover:underline">member sign-in</Link>.</p>

          <div className="mt-5 space-y-3">
            <div>
              <Label className="text-neutral-300">Admin email</Label>
              <Input type="email" required autoComplete="username" value={form.email}
                onChange={(e)=>setForm({...form, email:e.target.value})}
                className="mt-1 bg-neutral-800 border-neutral-700 text-neutral-100 focus-visible:ring-primary"
                data-testid="admin-email"/>
            </div>
            <div>
              <Label className="text-neutral-300">Password</Label>
              <Input type="password" required autoComplete="current-password" value={form.password}
                onChange={(e)=>setForm({...form, password:e.target.value})}
                className="mt-1 bg-neutral-800 border-neutral-700 text-neutral-100 focus-visible:ring-primary"
                data-testid="admin-password"/>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 rounded-full" data-testid="admin-login-submit">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Signing in…</> : <><Lock className="w-4 h-4 mr-2"/>Sign in as administrator</>}
          </Button>
          <div className="text-center mt-4">
            <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">← Back to homepage</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
